import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import CircularProgress from '@mui/material/CircularProgress'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { Link } from 'react-router-dom'
import { useCategoryContext } from 'context/CategoryContext'
import { useGetStoreCategoryProducts } from 'hooks/useStore'
import { currencies } from 'common/constants'
import { colors } from 'styles/colors'
import CustomText from '../../common/CustomText/CustomText'
import TitleText from '../../common/CustomText/TitleText'

const PAGE_SIZE = 30
const LOAD_MORE_THRESHOLD = 0.92

const StoreProductCardSkeleton = () => {
  return (
    <Card sx={{ width: 200, borderRadius: 3, boxShadow: 3 }}>
      <Skeleton variant="rectangular" width={200} height={200} />
      <Stack p={1} gap={0.8}>
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="55%" height={22} />
      </Stack>
    </Card>
  )
}

// The product as this store sells it: what it is, and the store's own price and
// quantity for it.
const StoreProductCard = React.memo(({ storeId, storeProduct }: { storeId: string, storeProduct: any }) => {
  const product = storeProduct.product
  // The product's translations never leave the server; the name comes as
  // `info`, in the admin's own language.
  const name = product.info?.name
  const image = product.avatar?.medium ?? undefined

  return (
    <Card
      component={Link}
      to={`/stores/all-stores/${storeId}/store-products/${storeProduct.id}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'block',
        width: 200,
        borderRadius: 3,
        boxShadow: 3,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {image ? (
        <CardMedia
          component="img"
          image={image}
          alt={name}
          sx={{ width: 200, height: 200, objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            width: 200,
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.topBackground,
          }}
        >
          <CustomText text="No image" fontSize={13} color={colors.gray} />
        </Box>
      )}
      <Stack p={1} gap={0.8}>
        <TitleText
          text={name ?? '—'}
          title={name ?? ''}
          fontWeight={600}
          textAlign="left"
          fontSize={14}
          color={colors.black}
          lineHeight={1}
          noWrap
        />
        <Box display="flex" justifyContent="space-between">
          <CustomText
            text={`${currencies.shekel}${Number(storeProduct.price).toFixed(2)}`}
            fontSize={13}
            fontWeight={600}
            color={colors.primary}
          />
          <CustomText
            text={`Quantity ${storeProduct.quantity}`}
            fontSize={13}
            color={colors.gray}
          />
        </Box>
      </Stack>
    </Card>
  )
})

type Props = {
  storeId: string
}

const StoreProductCarousel = ({ storeId }: Props) => {
  const { category } = useCategoryContext()

  const [page, setPage] = useState(1)
  const [storeProducts, setStoreProducts] = useState<any[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const loadingRef = useRef(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    loop: false,
    watchResize: false,
    watchSlides: true,
  })

  const { refetch } = useGetStoreCategoryProducts(
    {
      storeId,
      categoryId: category.id,
      page,
      size: PAGE_SIZE,
    },
    {
      select: (res: any) => res.data,
      onSuccess: (items: any[]) => {
        setStoreProducts((prev) => {
          if (page === 1) return items || []

          const existing = new Set(prev.map((item) => item.id))
          const next = (items || []).filter((item) => !existing.has(item.id))
          return [...prev, ...next]
        })

        setHasMore(!!items && items.length === PAGE_SIZE)
        loadingRef.current = false
        setLoadingMore(false)
      },
      onError: () => {
        loadingRef.current = false
        setLoadingMore(false)
      },
    },
  )

  useEffect(() => {
    setPage(1)
    setStoreProducts([])
    setHasMore(true)
    setLoadingMore(true)
    loadingRef.current = true
    refetch()
  }, [storeId, category.id, refetch])

  useEffect(() => {
    if (page === 1) return
    refetch()
  }, [page, refetch])

  const loadNextPage = useCallback(() => {
    if (loadingRef.current || loadingMore || !hasMore) return

    loadingRef.current = true
    setLoadingMore(true)
    setPage((prev) => prev + 1)
  }, [loadingMore, hasMore])

  const onScroll = useCallback(
    (api: EmblaCarouselType) => {
      if (loadingRef.current || loadingMore || !hasMore) return

      if (api.scrollProgress() >= LOAD_MORE_THRESHOLD) {
        loadNextPage()
      }
    },
    [loadingMore, hasMore, loadNextPage],
  )

  useEffect(() => {
    if (!emblaApi) return

    const handler = () => onScroll(emblaApi)

    emblaApi.on('scroll', handler)
    return () => {
      emblaApi.off('scroll', handler)
    }
  }, [emblaApi, onScroll])

  const isInitialLoading = loadingMore && storeProducts.length === 0

  return (
    <Box>
      <Box className="embla" sx={{ width: '100%' }}>
        <Box className="embla__viewport" ref={emblaRef} sx={{ overflow: 'hidden', px: 2 }}>
          <Box className="embla__container" sx={{ display: 'flex', gap: 2 }}>
            {isInitialLoading
              ? Array.from({ length: 10 }).map((_value, index) => (
                <Box
                  key={`initial-skeleton-${index}`}
                  className="embla__slide"
                  sx={{ flex: '0 0 auto', minWidth: 0, marginTop: 2, marginBottom: 2 }}
                >
                  <StoreProductCardSkeleton />
                </Box>
              ))
              : storeProducts.map((storeProduct, index) => (
                <Box
                  className="embla__slide"
                  key={storeProduct.id}
                  sx={{
                    flex: '0 0 auto',
                    minWidth: 0,
                    marginRight: index === storeProducts.length - 1 ? 2 : 0,
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                >
                  <StoreProductCard storeId={storeId} storeProduct={storeProduct} />
                </Box>
              ))}

            {!isInitialLoading && storeProducts.length === 0 && (
              <Box py={4} px={1}>
                <CustomText text="This store has no products here" fontSize={14} color={colors.gray} />
              </Box>
            )}

            {!isInitialLoading && hasMore && (
              <Box
                className="embla__slide"
                sx={{
                  flex: '0 0 48px',
                  minWidth: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                  marginBottom: 2,
                  marginRight: 2,
                }}
              >
                {loadingMore ? <CircularProgress size={22} /> : null}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default StoreProductCarousel
